import { catchError, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';

import {
  TargetStatsType,
  GetTargetStatsGQL,
  PartnershipTypeEnum,
  PartnershipNetworkType,
  InternalProductFilterType,
  InternalProductWithFavoriteStatusType,
  InternalProductForPartnershipNetworkFilterInput,
  InternalProductWithoutClassWithFavoriteStatusType,
  GetPartnershipNetworksByTargetAndPartnershipPaginationGQL,
  PartnershipNetworkWithLinkedAccountStatusAndSettingsType,
  GetPartnershipNetworksByTargetAndPartnershipWithSettingsPaginationGQL,
  GetSimpleProductsForPartnerhipNetworkWithRatingsAndFavoriteStatusAndRemunerationsAndFilterGQL,
  SimpleProductsForPartnerhipNetworkWithRatingsAndFavoriteStatusAndRemunerationsAndFilterPaginationType,
  InternalProductForPartnershipNetworkWithTargetPictureAndRatingsPaginateWithFavoriteStatusWithFilterType,
} from '@sifca-monorepo/widget-generator';
import { GetStockByTargetAndBarcodeGQL } from '@sifca-monorepo/terminal-generator';
import {
  StockType,
  ProductStatusEnum,
  GetSimpleProductsByTargetGroupedByClassWithFavoriteStatusGQL,
  GetSimpleProductWithProductAttributesAndFavoriteStatusGQL,
} from '@sifca-monorepo/ecommerce-generator';

@Injectable({
  providedIn: 'root',
})
export class MobileMarketPlaceService {
  private isLastPage: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private isLastPartners: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private loadingProduct: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private loadingPartners: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private loadingProducts: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private loadingProductsGroup: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private stats: BehaviorSubject<TargetStatsType> = new BehaviorSubject<TargetStatsType>(null);
  private productsGroup: BehaviorSubject<InternalProductWithoutClassWithFavoriteStatusType[]> = new BehaviorSubject<
    InternalProductWithoutClassWithFavoriteStatusType[]
  >(null);
  private filter: BehaviorSubject<InternalProductFilterType> = new BehaviorSubject<InternalProductFilterType>(null);
  private partners: BehaviorSubject<PartnershipNetworkWithLinkedAccountStatusAndSettingsType[]> = new BehaviorSubject<
    PartnershipNetworkWithLinkedAccountStatusAndSettingsType[]
  >(null);
  private product: BehaviorSubject<InternalProductWithFavoriteStatusType> = new BehaviorSubject<InternalProductWithFavoriteStatusType>(null);
  private filtredProducts: BehaviorSubject<SimpleProductsForPartnerhipNetworkWithRatingsAndFavoriteStatusAndRemunerationsAndFilterPaginationType> =
    new BehaviorSubject<SimpleProductsForPartnerhipNetworkWithRatingsAndFavoriteStatusAndRemunerationsAndFilterPaginationType>(null);

  pageIndex = 0;
  pageLimit = 10;
  searchString: any;
  partnersPageIndex = 0;
  partnersPageLimit = 10;
  apiFilter: InternalProductForPartnershipNetworkFilterInput = {};

  get productsGroup$(): Observable<InternalProductWithoutClassWithFavoriteStatusType[]> {
    return this.productsGroup.asObservable();
  }

  get product$(): Observable<InternalProductWithFavoriteStatusType> {
    return this.product.asObservable();
  }

  get stats$(): Observable<TargetStatsType> {
    return this.stats.asObservable();
  }

  get loadingPartners$(): Observable<boolean> {
    return this.loadingPartners.asObservable();
  }

  get loadingProduct$(): Observable<boolean> {
    return this.loadingProduct.asObservable();
  }

  get filtredProducts$(): Observable<SimpleProductsForPartnerhipNetworkWithRatingsAndFavoriteStatusAndRemunerationsAndFilterPaginationType> {
    return this.filtredProducts.asObservable();
  }
  set filtredProducts$(value: any) {
    this.filtredProducts.next(value);
  }

  get isLastPage$(): Observable<boolean> {
    return this.isLastPage.asObservable();
  }

  get isLastPartners$(): Observable<boolean> {
    return this.isLastPartners.asObservable();
  }

  get loadingProducts$(): Observable<boolean> {
    return this.loadingProducts.asObservable();
  }
  set loadingProducts$(value: any) {
    this.loadingProducts.next(value);
  }

  set filter$(value: any) {
    this.filter.next(value);
  }

  get filter$(): Observable<InternalProductFilterType> {
    return this.filter.asObservable();
  }

  get partners$(): Observable<PartnershipNetworkWithLinkedAccountStatusAndSettingsType[]> {
    return this.partners.asObservable();
  }

  get loadingProductsGroup$(): Observable<boolean> {
    return this.loadingProductsGroup.asObservable();
  }

  constructor(
    private getTargetStatsGQL: GetTargetStatsGQL,
    private getStockByTargetAndBarcodeGQL: GetStockByTargetAndBarcodeGQL,
    private getSimpleProductsByTargetGroupedByClassWithFavoriteStatusGQL: GetSimpleProductsByTargetGroupedByClassWithFavoriteStatusGQL,
    private getSimpleProductWithProductAttributesAndFavoriteStatusGQL: GetSimpleProductWithProductAttributesAndFavoriteStatusGQL,
    private getPartnershipNetworksByTargetAndPartnershipPaginationGQL: GetPartnershipNetworksByTargetAndPartnershipPaginationGQL,
    private getPartnershipNetworksByTargetAndPartnershipWithSettingsPaginationGQL: GetPartnershipNetworksByTargetAndPartnershipWithSettingsPaginationGQL,
    private getSimpleProductsForPartnerhipNetworkWithRatingsAndFavoriteStatusAndRemunerationsAndFilterGQL: GetSimpleProductsForPartnerhipNetworkWithRatingsAndFavoriteStatusAndRemunerationsAndFilterGQL,
  ) {}

  getStockByTargetAndBarcode(barcode: string, pos: string): Observable<StockType> {
    return this.getStockByTargetAndBarcodeGQL.fetch({ target: { pos }, barcode: barcode }).pipe(
      map(({ data }: any) => {
        return data.getStockByTargetAndBarcodeCache;
      }),
    );
  }

  getSimpleProductsByTargetGroupedByClassWithFavoriteStatus(pos: string): Observable<InternalProductWithoutClassWithFavoriteStatusType[]> {
    this.loadingProductsGroup.next(true);
    return this.getSimpleProductsByTargetGroupedByClassWithFavoriteStatusGQL
      .fetch({
        target: { pos },
        status: [ProductStatusEnum.ACTIVE],
      })
      .pipe(
        catchError(() => {
          this.loadingProductsGroup.next(false);
          return of(null);
        }),
        map(({ data }: any) => {
          this.loadingProductsGroup.next(false);
          this.productsGroup.next(data?.getSimpleProductsByTargetGroupedByClassWithFavoriteStatus.FEATURED_PRODUCTS);
          return data?.getSimpleProductsByTargetGroupedByClassWithFavoriteStatus.FEATURED_PRODUCTS;
        }),
      );
  }

  getSimpleProductWithProductAttributesAndFavoriteStatus(id: string): Observable<InternalProductWithFavoriteStatusType> {
    const language = localStorage.getItem('elvklang');
    this.loadingProduct.next(true);
    return this.getSimpleProductWithProductAttributesAndFavoriteStatusGQL
      .fetch({
        id,
        ...(language && language !== 'none' ? { language } : {}),
      })
      .pipe(
        map(({ data }: any) => {
          this.loadingProduct.next(false);
          this.product.next(data.getSimpleProductWithProductAttributesAndFavoriteStatus);
          return data.getSimpleProductWithProductAttributesAndFavoriteStatus;
        }),
      );
  }

  getTargetStats(): Observable<TargetStatsType> {
    return this.getTargetStatsGQL
      .fetch({
        target: { pos: localStorage.getItem('posId') },
      })
      .pipe(
        catchError((error) => {
          throw error;
        }),
        map(({ data }: any) => {
          if (data) {
            this.stats.next(data.getTargetStats);
            return data.getTargetStats;
          }
        }),
      );
  }

  resetPartnersPagination(): void {
    this.partnersPageIndex = 0;
    this.isLastPartners.next(false);
    this.partners.next(null);
  }

  getPartnershipNetworksByTargetAndPartnershipWithSettingsPagination(
    partnership: PartnershipTypeEnum,
    reset = true,
  ): Observable<PartnershipNetworkWithLinkedAccountStatusAndSettingsType[]> {
    if (reset) {
      this.loadingPartners.next(true);
    }
    return this.getPartnershipNetworksByTargetAndPartnershipWithSettingsPaginationGQL
      .fetch({
        target: { pos: localStorage.getItem('posId') },
        pagination: {
          page: this.partnersPageIndex,
          limit: this.partnersPageLimit,
        },
        partnership,
      })
      .pipe(
        catchError((error) => {
          this.loadingPartners.next(false);
          throw error;
        }),
        map(({ data }: any) => {
          if (data) {
            this.isLastPartners.next(data.getPartnershipNetworksByTargetAndPartnershipWithSettingsPagination.isLast);
            this.loadingPartners.next(false);
            this.partners.next(data.getPartnershipNetworksByTargetAndPartnershipWithSettingsPagination.objects);
            return data.getPartnershipNetworksByTargetAndPartnershipWithSettingsPagination.objects;
          }
        }),
      );
  }

  getPartnershipNetworksByTargetAndPartnershipPagination(partnership: PartnershipTypeEnum): Observable<PartnershipNetworkType[]> {
    this.loadingPartners.next(true);
    return this.getPartnershipNetworksByTargetAndPartnershipPaginationGQL
      .fetch({
        target: { pos: localStorage.getItem('posId') },
        pagination: {
          limit: 50,
        },
        partnership,
      })
      .pipe(
        catchError((error) => {
          this.loadingPartners.next(false);
          throw error;
        }),
        map(({ data }: any) => {
          if (data) {
            this.loadingPartners.next(false);
            this.partners.next(data.getPartnershipNetworksByTargetAndPartnershipPagination.objects);
            return data.getPartnershipNetworksByTargetAndPartnershipPagination.objects;
          }
        }),
      );
  }

  getSimpleProductsForPartnerhipNetworkWithRatingsAndFavoriteStatusAndRemunerationsAndFilter(
    isInitial = true,
    filter?: InternalProductForPartnershipNetworkFilterInput,
    limit?: number,
  ): Observable<InternalProductForPartnershipNetworkWithTargetPictureAndRatingsPaginateWithFavoriteStatusWithFilterType> {
    if (isInitial) {
      this.loadingProducts.next(true);
    }
    return this.getSimpleProductsForPartnerhipNetworkWithRatingsAndFavoriteStatusAndRemunerationsAndFilterGQL
      .fetch({
        input: {
          target: { pos: localStorage.getItem('posId') },
          pagination: { page: this.pageIndex, limit: limit ?? this.pageLimit },
          ...(filter
            ? {
                filter: {
                  ...(filter?.partners?.pos?.length ? { partners: { pos: filter?.partners?.pos } } : {}),
                  ...(filter?.fromPrice && +filter.fromPrice > 0 ? { fromPrice: filter.fromPrice } : ''),
                  ...(filter?.toPrice && +filter?.toPrice > 0 ? { toPrice: filter.toPrice } : ''),
                },
              }
            : {}),
        },
      })
      .pipe(
        map(({ data }: any) => {
          if (isInitial) {
            this.filter.next(data.getSimpleProductsForPartnerhipNetworkWithRatingsAndFavoriteStatusAndRemunerationsAndFilter.filter);
          }
          this.loadingProducts.next(false);
          this.isLastPage.next(data.getSimpleProductsForPartnerhipNetworkWithRatingsAndFavoriteStatusAndRemunerationsAndFilter.isLast);
          this.filtredProducts.next(data.getSimpleProductsForPartnerhipNetworkWithRatingsAndFavoriteStatusAndRemunerationsAndFilter);
          return data.getSimpleProductsForPartnerhipNetworkWithRatingsAndFavoriteStatusAndRemunerationsAndFilter;
        }),
      );
  }
}
